import { CONTRACT_PRINT_CSS, CONTRACT_PRINT_ROOT_HTML } from './contractTemplate';
import { compute, money, formatDate, parseDate, DEFAULT_SERVICE_CONTRACT_FEE } from './bhphMath';

export const SELLER_TITLES_EMAIL = 'titles@thewholesalecars.com';

function companyDisplayName(co) {
  const legal = (co.coLegal || co.legal_name || '').trim();
  const dba = (co.coDba || co.dba || '').trim();
  if (legal && dba) return `${legal} d/b/a ${dba}`;
  return legal || dba || '[Seller Legal Name]';
}

function sellerEmail(company, form) {
  return (
    (company?.email || form?.coEmail || form?.email || '').trim() || SELLER_TITLES_EMAIL
  );
}

function scrubDashes(s) {
  return String(s ?? '')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/-/g, '-')
    .replace(/-/g, '-');
}

function titleCaseSlug(s) {
  return String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function paymentKindLabel(kind) {
  if (kind === 'tax_title') return 'Tax / Title / License';
  if (!kind) return 'Payment';
  return titleCaseSlug(kind);
}

function paymentMethodLabel(method) {
  if (!method) return 'Card Draft';
  return titleCaseSlug(method);
}

function receiptDateLabel(raw) {
  const d = parseDate(raw) || (raw instanceof Date ? raw : null);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function receiptLetterhead() {
  return {
    name: 'Alexis Motorcar LLC',
    specialist: 'Gina R.',
    title: 'Payments and Collections Specialist',
    addr: '4001 W. Alexis Rd., Toledo, OH 43623',
    phone: '(419) 364-7901',
    email: 'titles@thewholesalecars.com',
  };
}

function companyAddressLine(co) {
  const street = co.coStreet || co.street || '';
  const cityLine = [co.coCity || co.city, co.coState || co.state, co.coZip || co.zip]
    .filter(Boolean)
    .join(', ');
  return [street, cityLine].filter(Boolean).join(' · ') || '-';
}

function buyerAddressLine(form) {
  const street = form.buyerStreet || '';
  const cityLine = [form.buyerCity, form.buyerState, form.buyerZip].filter(Boolean).join(', ');
  return [street, cityLine].filter(Boolean).join(', ') || '-';
}

function vehicleLine(form) {
  return [form.vehYear, form.vehMake, form.vehModel, form.vehColor].filter(Boolean).join(' ') || '-';
}

function preferredContact(form) {
  const modes = [];
  if (form.contactEmail) modes.push('Email');
  if (form.contactSms) modes.push('SMS/Text');
  if (form.contactVoice) modes.push('Phone call');
  return modes.length ? modes.join(', ') : '-';
}

function dash(v) {
  if (v == null || v === '') return '-';
  return scrubDashes(v);
}

export function buildBindMap(company, form) {
  const co = {
    coLegal: company?.legal_name || form.coLegal || '',
    coDba: company?.dba || form.coDba || '',
    coStreet: company?.street || form.coStreet || '',
    coCity: company?.city || form.coCity || '',
    coState: company?.state || form.coState || 'OH',
    coZip: company?.zip || form.coZip || '',
    coPhone: company?.phone || form.coPhone || '',
    coEmail: sellerEmail(company, form),
    coAlertEmail: company?.alert_email || form.coAlertEmail || sellerEmail(company, form),
    coWebsite: company?.website || form.coWebsite || '',
    coDealerNo: company?.dealer_number || form.coDealerNo || '',
    coCounty: company?.county || form.coCounty || 'Lucas',
  };

  const serviceFeeDefault =
    form.serviceContractFee ?? company?.service_contract_fee ?? DEFAULT_SERVICE_CONTRACT_FEE;

  const merged = {
    ...form,
    lateFee: form.lateFee ?? company?.late_fee ?? 35,
    declineFee: form.declineFee ?? company?.decline_fee ?? 10,
    repoFee: form.repoFee ?? company?.repo_fee ?? 199,
    impoundFee: form.impoundFee ?? company?.impound_admin_fee ?? 299,
    lateFeeStartDays: form.lateFeeStartDays ?? company?.late_fee_start_days ?? 5,
    lateFeeRepeatDays: form.lateFeeRepeatDays ?? company?.late_fee_repeat_days ?? 3,
    serviceContractFee: serviceFeeDefault,
    apr:
      form.apr != null && form.apr !== ''
        ? form.apr
        : company?.default_apr != null
          ? Number(company.default_apr)
          : 0,
  };

  const c = compute(merged);
  const gps = !!merged.gpsInstalled;

  const binds = {
    coName: companyDisplayName(co),
    coAddress: companyAddressLine(co),
    coPhone: co.coPhone || '-',
    coEmail: co.coEmail || SELLER_TITLES_EMAIL,
    coAlertEmail: co.coAlertEmail || co.coEmail || SELLER_TITLES_EMAIL,
    coWebsite: co.coWebsite || '-',
    coDealerNo: co.coDealerNo || '-',
    coCounty: co.coCounty || 'Lucas',
    saleDate: formatDate(parseDate(merged.saleDate)) || merged.saleDate || '-',
    buyerName: merged.buyerName || '-',
    buyerDob: merged.buyerDob || '-',
    buyerDl: [merged.buyerDl, merged.buyerDlState].filter(Boolean).join(' ') || '-',
    buyerAddress: buyerAddressLine(merged),
    buyerPhone: merged.buyerPhone || '-',
    buyerPhoneAlt: merged.buyerPhoneAlt || '-',
    buyerEmail: merged.buyerEmail || '-',
    preferredContact: preferredContact(merged),
    emergency: [merged.emergencyName, merged.emergencyPhone].filter(Boolean).join(' · ') || '-',
    coBuyer: merged.coBuyerName || '-',
    coBuyerPhone: merged.coBuyerPhone || '-',
    coBuyerEmail: merged.coBuyerEmail || '-',
    vehYear: merged.vehYear || '-',
    vehMake: merged.vehMake || '-',
    vehModel: merged.vehModel || '-',
    vehColor: merged.vehColor || '-',
    vin: merged.vehVin || '-',
    vehicle: vehicleLine(merged),
    stock: merged.vehStock || '-',
    odometer: merged.vehOdometer || '-',
    firstDue: formatDate(c.firstDueDate) || merged.firstDue || '-',
    payoffDate: formatDate(c.payoffDate) || '-',
    taxTitleAmt: money(c.taxTitle),
    taxTitleDate: formatDate(parseDate(c.taxTitleDate || merged.taxTitleDate)) || '-',
    cashPrice: money(c.cash),
    cashPriceTotal: money(c.cashPriceTotal),
    downPayment: money(c.down),
    tradeDesc: merged.tradeDesc || '-',
    tradeAmt: money(c.trade),
    unpaidBalance: money(c.unpaidBalance),
    remainingAfterFirst: money(c.remainingAfterFirst),
    principal: money(c.principal),
    financeCharge: money(c.financeCharge),
    timeBalance: money(c.timeBalance),
    aprDisplay: c.aprDisplay || '0%',
    aprPercent: String(c.aprPercent ?? 0),
    interestTotal: money(c.interestTotal ?? c.financeCharge),
    principalPaidTotal: money(c.principalPaidTotal ?? c.principal),
    otherChargesTotal: money((c.other || 0) + (c.serviceOn ? c.serviceFee || 0 : 0)),
    numPayments: String(c.nPay || 0),
    payAmount: money(c.paymentAmt),
    lastPayment: money(c.lastPayment),
    lateFee: money(merged.lateFee),
    declineFee: money(merged.declineFee),
    repoFee: money(merged.repoFee),
    impoundFee: money(merged.impoundFee),
    lateFeeStartDays: String(merged.lateFeeStartDays ?? 5),
    lateFeeRepeatDays: String(merged.lateFeeRepeatDays ?? 3),
    serviceFee: c.serviceOn ? money(c.serviceFee) : '$0.00',
    serviceFeeOffered: money(serviceFeeDefault),
    gpsYesNo: gps
      ? 'This Vehicle may have been equipped with a GPS / electronic locating device.'
      : 'No GPS disclosure elected for this sale beyond the general notice below.',
    cardName: merged.cardName || '-',
    cardBrand: merged.cardBrand || '-',
    cardLast4: merged.cardLast4 || '-',
    cardExp: merged.cardExp || '-',
    cardZip: merged.cardZip || '-',
    sigDate: formatDate(parseDate(merged.saleDate)) || formatDate(new Date()) || '-',
    keyDatesBalanceNote: buildKeyDatesBalanceNote(c.schedule),
  };

  for (let i = 1; i <= 6; i++) {
    binds[`fee${i}Label`] = merged[`fee${i}Label`] || '-';
    binds[`fee${i}Amt`] = money(merged[`fee${i}Amt`]);
  }

  Object.keys(binds).forEach((k) => {
    binds[k] = dash(binds[k]);
  });

  return { binds, compute: c, company: co };
}

function scheduleRowType(row) {
  if (row.kind === 'tax_title') return 'Tax/Title/License';
  return row.label || 'Installment';
}

function buildKeyDatesBalanceNote(schedule) {
  const rows = schedule || [];
  if (!rows.length) {
    return 'First drafts appear after APR and payment terms are set.';
  }
  let afterFirst = null;
  let afterTtl = null;
  let afterSecond = null;
  let secondDate = null;
  let ttlDate = null;
  let installmentSeen = 0;
  for (const row of rows) {
    if (row.kind === 'tax_title') {
      afterTtl = row.balanceAfter;
      ttlDate = formatDate(row.dateObj || parseDate(row.date));
      continue;
    }
    installmentSeen += 1;
    if (installmentSeen === 1) afterFirst = row.balanceAfter;
    if (installmentSeen === 2) {
      afterSecond = row.balanceAfter;
      secondDate = formatDate(row.dateObj || parseDate(row.date));
      break;
    }
  }
  if (afterSecond != null && ttlDate) {
    return (
      `After Tax/Title/License on ${ttlDate} (installment skipped that week) and Installment 2 on ${secondDate}, ` +
      `remaining time balance owed is ${money(afterSecond)} (includes remaining finance charge / interest in the time balance).`
    );
  }
  if (afterTtl != null && afterFirst != null) {
    return (
      `After Installment 1 and Tax/Title/License (${ttlDate || 'TTL week'}; installment skipped that week), ` +
      `remaining time balance is ${money(afterTtl)}.`
    );
  }
  return 'See the Payment Schedule for full biweekly drafts. Tax/Title/License week skips the regular installment.';
}

function fillBinds(root, binds) {
  root.querySelectorAll('[data-bind]').forEach((el) => {
    const key = el.getAttribute('data-bind');
    const v = binds[key];
    el.textContent = v == null || v === '' ? '-' : String(v);
  });
}

function appendScheduleRows(tbody, schedule, { limit = null, emptyColspan = 8 } = {}) {
  if (!tbody) return;
  tbody.innerHTML = '';
  const rows = schedule || [];
  const slice = limit != null ? rows.slice(0, limit) : rows;
  slice.forEach((row) => {
    const tr = document.createElement('tr');
    const type = scheduleRowType(row);
    const interest = row.kind === 'tax_title' ? 0 : row.interest || 0;
    const principalPortion = row.kind === 'tax_title' ? 0 : row.principalPortion || 0;
    tr.innerHTML = `
      <td>${row.num}</td>
      <td>${formatDate(row.dateObj || parseDate(row.date))}</td>
      <td>${type}</td>
      <td class="num">${money(row.amount)}</td>
      <td class="num">${money(interest)}</td>
      <td class="num">${money(principalPortion)}</td>
      <td>${row.method || 'CARD DRAFT'}</td>
      <td class="num">${money(row.balanceAfter)}</td>
    `;
    tbody.appendChild(tr);
  });
  if (!slice.length) {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td colspan="${emptyColspan}">No schedule yet (enter APR + # of payments, or payment amount).</td>`;
    tbody.appendChild(tr);
  }
}

function fillSchedule(root, schedule) {
  appendScheduleRows(root.querySelector('#scheduleBody'), schedule);
}

/** First drafts through Installment 2 (includes Tax/Title/License skip week). */
function fillKeyDatesSchedule(root, schedule) {
  const tbody = root.querySelector('#keyDatesScheduleBody');
  if (!tbody) return;
  const rows = schedule || [];
  let cutoff = 0;
  let installments = 0;
  for (let i = 0; i < rows.length; i++) {
    cutoff = i + 1;
    if (rows[i].kind !== 'tax_title') {
      installments += 1;
      if (installments >= 2) break;
    }
  }
  if (!cutoff) cutoff = Math.min(3, rows.length);
  appendScheduleRows(tbody, rows, { limit: cutoff || 3 });
}

function fillAmort(root, schedule) {
  const tbody = root.querySelector('#amortBody');
  if (!tbody) return;
  tbody.innerHTML = '';
  (schedule || []).forEach((row) => {
    const tr = document.createElement('tr');
    const type = scheduleRowType(row);
    tr.innerHTML = `
      <td>${row.num}</td>
      <td>${formatDate(row.dateObj || parseDate(row.date))}</td>
      <td>${type}</td>
      <td class="num">${money(row.amount)}</td>
      <td class="num">${money(row.interest || 0)}</td>
      <td class="num">${money(row.principalPortion || 0)}</td>
      <td class="num">${money(
        row.principalBalance != null ? row.principalBalance : row.balanceAfter
      )}</td>
    `;
    tbody.appendChild(tr);
  });
  if (!(schedule || []).length) {
    const tr = document.createElement('tr');
    tr.innerHTML =
      '<td colspan="7">Set APR and payment count on the account, then print again.</td>';
    tbody.appendChild(tr);
  }
}

function applyServiceVisibility(root, serviceOn) {
  root.querySelectorAll('[data-service-only]').forEach((el) => {
    el.style.display = serviceOn ? '' : 'none';
  });
  root.querySelectorAll('[data-decline-only]').forEach((el) => {
    el.style.display = serviceOn ? 'none' : '';
  });
  root.querySelectorAll('[data-repairs-service]').forEach((el) => {
    el.style.display = serviceOn ? '' : 'none';
  });
  root.querySelectorAll('[data-repairs-asis]').forEach((el) => {
    el.style.display = serviceOn ? 'none' : '';
  });
  root.querySelectorAll('[data-service-row]').forEach((el) => {
    el.style.display = serviceOn ? '' : 'none';
  });
}

export function buildPrintHtml(company, form) {
  const { binds, compute: c } = buildBindMap(company, form);
  const wrap = document.createElement('div');
  wrap.innerHTML = CONTRACT_PRINT_ROOT_HTML;
  const root = wrap.querySelector('#print-root') || wrap.firstElementChild;
  if (!root) return null;
  fillBinds(root, binds);
  fillSchedule(root, c.schedule);
  fillKeyDatesSchedule(root, c.schedule);
  fillAmort(root, c.schedule);
  applyServiceVisibility(root, !!form.serviceContract);
  return {
    html:
      '<!DOCTYPE html><html><head><meta charset="utf-8" />' +
      '<title>BHPH Contract Package - TIL + RISC</title>' +
      '<style>' +
      CONTRACT_PRINT_CSS +
      '\nbody{background:#fff!important;margin:0;}' +
      '\n.no-print{display:none!important;}' +
      '\n#print-root{display:block!important;padding:0!important;max-width:none!important;}' +
      '\n.doc-page[style*="display: none"]{display:none!important;}' +
      '</style></head><body>' +
      root.innerHTML +
      '</body></html>',
    binds,
    compute: c,
  };
}

export function printBhphPackage(company, form) {
  const built = buildPrintHtml(company, form);
  if (!built) {
    alert('Print package template not found.');
    return;
  }
  if (!(built.compute?.apr > 0) || !(built.compute?.financeCharge > 0)) {
    const go = window.confirm(
      'This account shows 0% APR / $0 finance charge.\n\n' +
        'Truth in Lending will print with $0.00 finance charge.\n\n' +
        'To charge interest: Edit the account → set APR (e.g. 18) and # of payments → Save → Print again.\n\n' +
        'Print anyway with $0 APR?'
    );
    if (!go) return;
  }
  const w = window.open('', '_blank');
  if (!w) {
    alert(
      'Pop-up blocked. Allow pop-ups for localhost, then click Print again.'
    );
    return;
  }
  w.document.open();
  w.document.write(built.html);
  w.document.close();
  const doPrint = () => {
    try {
      w.focus();
      w.print();
    } catch (e) {
      alert('Print failed. Open the new tab and press Cmd+P.');
    }
  };
  if (w.document.readyState === 'complete') setTimeout(doPrint, 250);
  else w.onload = () => setTimeout(doPrint, 250);
}

export function printRepoPackage(company, account, repo) {
  const coName = companyDisplayName({
    coLegal: company?.legal_name,
    coDba: company?.dba,
  });
  const coAddr = [
    company?.street,
    [company?.city, company?.state || 'OH', company?.zip].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
  const email = sellerEmail(company);
  const repoCo =
    (repo?.repoCompanyName || company?.repo_company_name || '').trim() ||
    '________________________________';
  const repoPhone =
    (repo?.repoCompanyPhone || company?.repo_company_phone || '').trim() || '____________________';
  const repoAddr =
    (repo?.repoCompanyAddress || company?.repo_company_address || '').trim() ||
    '________________________________________________';
  const buyerAddr =
    [account.buyer_street, account.buyer_city, account.buyer_state, account.buyer_zip]
      .filter(Boolean)
      .join(', ') || '-';
  const vehicle = [account.veh_year, account.veh_make, account.veh_model, account.veh_color]
    .filter(Boolean)
    .join(' ');
  const reason = scrubDashes(
    repo.reason || 'Default under retail installment contract for nonpayment / related default.'
  );
  const sigDate =
    formatDate(parseDate(repo.assignedDate)) ||
    formatDate(parseDate(repo.repoDate)) ||
    formatDate(new Date()) ||
    '-';
  const html = scrubDashes(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Repossession Order</title>
<style>
body{font-family:"Times New Roman",Times,serif;margin:0.65in;color:#111;font-size:11pt;line-height:1.4}
h1{font-size:15pt;text-align:center;margin:0 0 0.35rem;font-weight:700}
h2{font-size:11.5pt;margin:0.85rem 0 0.3rem;border-bottom:1px solid #333;padding-bottom:0.15rem}
.meta{font-size:9.5pt;margin-bottom:0.65rem}
.box{border:1.5px solid #111;padding:0.55rem 0.65rem;margin:0.5rem 0}
.row{margin:0.2rem 0}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:0.75rem}
.sig{margin-top:1.75rem;display:grid;grid-template-columns:1fr 1fr;gap:2rem}
.line{border-top:1px solid #111;margin-top:2.25rem;padding-top:0.25rem;font-size:9pt}
.sig-date{margin-top:0.35rem;font-size:9.5pt}
.page-break{page-break-before:always}
.fill{border-bottom:1px solid #111;min-height:1.1rem;margin:0.35rem 0}
.small{font-size:9pt;color:#333}
</style></head><body>
<section>
  <h1>Repossession Order</h1>
  <div class="meta">
    <strong>Seller / Lienholder</strong><br/>
    ${coName}<br/>
    ${coAddr || '-'}<br/>
    Tel ${company?.phone || '-'} · ${email}<br/>
    County: ${company?.county || 'Lucas'}, Ohio · Dealer # ${company?.dealer_number || '-'}
  </div>
  <div class="box">
    <strong>Assigned repossession company</strong>
    <div class="row">Company name: ${repoCo}</div>
    <div class="row">Phone: ${repoPhone}</div>
    <div class="row">Address: ${repoAddr}</div>
    <div class="row">Agent / contact: ${repo.repoAgent || '____________________'} · Date assigned: ${repo.assignedDate || '____________'}</div>
  </div>
  <div class="box">
    <div class="row"><strong>Account #:</strong> ${account.id}</div>
    <div class="row"><strong>Buyer:</strong> ${account.buyer_name || '-'}</div>
    <div class="row"><strong>Phone:</strong> ${account.buyer_phone || '-'} · Alt: ${account.buyer_phone_alt || '-'}</div>
    <div class="row"><strong>Buyer address:</strong> ${buyerAddr}</div>
    <div class="row"><strong>Vehicle:</strong> ${vehicle || '-'} · Color: ${account.veh_color || '-'}</div>
    <div class="row"><strong>VIN:</strong> ${account.veh_vin || '-'} · Stock: ${account.veh_stock || '-'}</div>
    <div class="row"><strong>Odometer (if known):</strong> ${account.veh_odometer || '____________'}</div>
  </div>
  <h2>Why this Vehicle is being repossessed</h2>
  <p>${reason}</p>
  <h2>Last payment</h2>
  <p>Date: ${repo.lastPaymentDate || '-'} · Amount: ${money(repo.lastPaymentAmount || 0)}</p>
  <h2>Amount to reinstate / get Vehicle back</h2>
  <p><strong>${money(repo.reinstateAmount || 0)}</strong></p>
  <p>${scrubDashes(repo.reinstateNotes || '')}</p>
  <h2>Customer instructions</h2>
  <p>${scrubDashes(repo.customerInstructions || 'Contact Seller immediately. Do not conceal or move the Vehicle.')}</p>
  <h2>Personal property</h2>
  <p>Personal property left in the Vehicle may be claimed by contacting Seller. Buyer may recover personal belongings as arranged with Seller.</p>
  <h2>Keys</h2>
  <p>If Buyer turns in the Vehicle keys to Seller, Buyer will be paid <strong>$100.00 cash</strong>.</p>
  <h2>Statutory cure</h2>
  <p class="small">ORC 1317.12 provides a limited statutory right to cure after repossession for covered retail installment contracts; that right cannot be waived. See the statutory cure acknowledgment in the contract package and any notices required by Ohio law.</p>
  <div class="sig">
    <div><div class="line">Seller authorization</div><div class="sig-date">Date: <strong>${sigDate}</strong></div></div>
    <div><div class="line">Repo company acknowledgment</div><div class="sig-date">Date: <strong>${sigDate}</strong></div></div>
  </div>
</section>
<section class="page-break">
  <h1>Customer Copy - Repossession Notice</h1>
  <div class="meta">
    <strong>${coName}</strong><br/>
    ${coAddr || '-'}<br/>
    Tel ${company?.phone || '-'} · ${email}
  </div>
  <p>Account #${account.id} · ${account.buyer_name || ''} · VIN ${account.veh_vin || ''}</p>
  <p>Your account is in repossession. Amount to reinstate: <strong>${money(repo.reinstateAmount || 0)}</strong>.</p>
  <p>Last payment: ${repo.lastPaymentDate || '-'} for ${money(repo.lastPaymentAmount || 0)}.</p>
  <p>Reason: ${reason}</p>
  <p>${scrubDashes(repo.customerInstructions || '')}</p>
  <p>Personal property: you may arrange pickup of belongings with Seller.</p>
  <p>If you turn in the Vehicle keys, you will be paid $100.00 cash.</p>
  <p class="small">Ohio law may provide a limited statutory right to cure after repossession (ORC 1317.12). Contact Seller at ${email} for questions about cure or reinstatement.</p>
  <div class="sig">
    <div><div class="line">Customer acknowledgment (optional)</div><div class="sig-date">Date: <strong>${sigDate}</strong></div></div>
  </div>
</section>
</body></html>`);

  const w = window.open('', '_blank');
  if (!w) {
    alert('Pop-up blocked.');
    return html;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch (_) {}
  }, 250);
  return html;
}

export function buildCustomerStatementHtml(company, account, delinquency) {
  const coName = companyDisplayName({
    coLegal: company?.legal_name,
    coDba: company?.dba,
  });
  const email = sellerEmail(company);
  const coAddr = [
    company?.street,
    [company?.city, company?.state || 'OH', company?.zip].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
  const buyerAddr =
    [account.buyer_street, account.buyer_city, account.buyer_state, account.buyer_zip]
      .filter(Boolean)
      .join(', ') || '-';
  const vehicle = [account.veh_year, account.veh_make, account.veh_model, account.veh_color]
    .filter(Boolean)
    .join(' ');
  const payments = (account.payments || []).filter((p) => !p.voided_at);
  const paymentRows = payments.length
    ? payments
        .slice()
        .sort((a, b) => String(a.paid_on || '').localeCompare(String(b.paid_on || '')))
        .map(
          (p) =>
            `<tr>
              <td>${String(p.paid_on || '').slice(0, 10) || '-'}</td>
              <td>${paymentKindLabel(p.kind)}</td>
              <td>${(p.method || 'card_draft').replace(/_/g, ' ')}</td>
              <td class="num">${money(p.amount)}</td>
              <td>${scrubDashes(p.notes || '')}</td>
            </tr>`
        )
        .join('')
    : '<tr><td colspan="5">No payments posted to date.</td></tr>';
  const totalPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const asOf = formatDate(new Date()) || new Date().toLocaleDateString();

  return scrubDashes(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Customer Account Statement</title>
<style>
body{font-family:"Times New Roman",Times,serif;margin:0.65in;color:#111;font-size:11pt;line-height:1.35}
h1{font-size:16pt;margin:0 0 0.25rem;text-align:center}
.sub{text-align:center;font-size:9.5pt;color:#333;margin-bottom:0.75rem}
.hdr{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:0.75rem}
.box{border:1.5px solid #111;padding:0.5rem 0.65rem}
.box h2{font-size:10.5pt;margin:0 0 0.35rem;text-transform:uppercase;letter-spacing:0.02em}
.row{margin:0.15rem 0}
table{width:100%;border-collapse:collapse;margin-top:0.45rem;font-size:10pt}
th,td{border:1px solid #444;padding:0.28rem 0.35rem;text-align:left;vertical-align:top}
th{background:#f0f0f0;font-weight:700}
td.num,th.num{text-align:right}
.summary{margin-top:0.65rem}
.footer{margin-top:0.85rem;font-size:9pt;color:#333}
</style></head><body>
<h1>Customer Account Statement</h1>
<div class="sub">Statement date: ${asOf} · Account #${account.id}</div>
<div class="hdr">
  <div class="box">
    <h2>Seller</h2>
    <div class="row"><strong>${coName}</strong></div>
    <div class="row">${coAddr || '-'}</div>
    <div class="row">Tel ${company?.phone || '-'} · ${email}</div>
  </div>
  <div class="box">
    <h2>Buyer</h2>
    <div class="row"><strong>${account.buyer_name || '-'}</strong></div>
    <div class="row">${buyerAddr}</div>
    <div class="row">Phone: ${account.buyer_phone || '-'} · Email: ${account.buyer_email || '-'}</div>
    ${account.co_buyer_name ? `<div class="row">Co-Buyer: ${account.co_buyer_name}</div>` : ''}
  </div>
</div>
<div class="box">
  <h2>Vehicle / account</h2>
  <div class="row"><strong>Vehicle:</strong> ${vehicle || '-'} · VIN ${account.veh_vin || '-'}</div>
  <div class="row"><strong>Stock:</strong> ${account.veh_stock || '-'} · Sale date: ${String(account.sale_date || '').slice(0, 10) || '-'}</div>
  <div class="row"><strong>Status:</strong> ${account.status || 'active'} · Days past due: ${delinquency?.daysPastDue ?? 0}</div>
  <div class="row"><strong>Biweekly installment:</strong> ${money(account.pay_amount)} · Tax/Title/License: ${money(account.tax_title_amt)}</div>
  <div class="row"><strong>First payment due:</strong> ${String(account.first_due || '').slice(0, 10) || '-'} · APR: ${account.apr != null ? account.apr : '-'}</div>
</div>
<table class="summary">
  <tr><th>Original time balance</th><td class="num">${money(account.time_balance)}</td></tr>
  <tr><th>Cash collected (posted payments)</th><td class="num">${money(delinquency?.collected ?? totalPaid)}</td></tr>
  <tr><th>Remaining principal / balance</th><td class="num">${money(delinquency?.remainingPrincipal ?? account.unpaid_balance)}</td></tr>
  <tr><th>Next unpaid due (if any)</th><td>${delinquency?.earliestUnpaidDue || '-'}</td></tr>
</table>
<h2 style="font-size:12pt;margin:0.85rem 0 0.25rem">Payment history</h2>
<table>
  <thead>
    <tr><th>Date paid</th><th>Type</th><th>Method</th><th class="num">Amount</th><th>Notes</th></tr>
  </thead>
  <tbody>${paymentRows}</tbody>
  <tfoot>
    <tr><th colspan="3">Total posted payments</th><th class="num">${money(totalPaid)}</th><th></th></tr>
  </tfoot>
</table>
<p class="footer">
  Card draft is the default payment method. For card updates or questions, email ${email} or call ${company?.phone || 'Seller'}.
  This statement reflects BHPH account activity only.
</p>
</body></html>`);
}

export function buildPaymentReceiptHtml(company, account, payment) {
  const head = receiptLetterhead(company);
  const paidOn = receiptDateLabel(payment?.paid_on);
  const kind = paymentKindLabel(payment?.kind === 'tax_title' ? 'tax_title' : payment?.kind || 'payment');
  const method = paymentMethodLabel(payment?.method || 'card_draft');
  const vehicle = [account.veh_year, account.veh_make, account.veh_model].filter(Boolean).join(' ') || '-';
  const vin = account.veh_vin || '';
  const paid = Number(payment?.amount) || 0;
  const bal = Number(account?.delinquency?.remainingPrincipal ?? account?.unpaid_balance);
  const remaining = Number.isFinite(bal) ? Math.max(0, Math.round(bal * 100) / 100) : null;

  return scrubDashes(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Payment Receipt</title>
<style>
@page{size:letter;margin:0.5in}
*{box-sizing:border-box}
body{font-family:Helvetica,Arial,sans-serif;margin:0;color:#111;font-size:10.5pt;line-height:1.25}
.wrap{max-width:420px;margin:0 auto}
.top{text-align:center;margin-bottom:10px}
.top h1{font-size:16pt;margin:0 0 2px}
.top p{margin:0;font-size:9pt;color:#333}
.title{text-align:center;font-size:12pt;font-weight:700;letter-spacing:0.08em;margin:10px 0 8px}
table{width:100%;border-collapse:collapse}
td{padding:4px 0;vertical-align:top}
td.l{color:#555;width:38%}
td.r{text-align:right;font-weight:700}
hr{border:0;border-top:1px solid #111;margin:8px 0}
.thanks{text-align:center;font-size:9.5pt;margin:12px 0 4px}
.contact{text-align:center;font-size:8.5pt;color:#333;line-height:1.35;margin:0}
</style></head><body>
<div class="wrap">
  <div class="top">
    <h1>${head.name}</h1>
    <p>${head.addr}</p>
    <p>${head.phone}</p>
  </div>
  <div class="title">RECEIPT</div>
  <table>
    <tr><td class="l">Date</td><td>${paidOn}</td></tr>
    <tr><td class="l">Received from</td><td>${account.buyer_name || '-'}</td></tr>
    <tr><td class="l">Vehicle</td><td>${vehicle}${vin ? `<br><span style="font-size:8.5pt;color:#555">VIN ${vin}</span>` : ''}</td></tr>
    <tr><td class="l">Type</td><td>${kind}</td></tr>
    <tr><td class="l">Method</td><td>${method}</td></tr>
  </table>
  <hr/>
  <table>
    <tr><td class="l">Amount paid</td><td class="r">${money(paid)}</td></tr>
    ${remaining != null ? `<tr><td class="l">Balance remaining</td><td class="r">${money(remaining)}</td></tr>` : ''}
  </table>
  ${payment?.notes ? `<p style="font-size:9pt;margin:8px 0 0">Notes: ${scrubDashes(payment.notes)}</p>` : ''}
  <p class="thanks">Thank you for your business.</p>
  <p class="contact">${head.specialist}<br>${head.title}<br>${head.name}<br>${head.addr}<br>${head.phone}<br>${head.email}</p>
</div>
</body></html>`);
}

export function buildInternalPnlHtml(company, account, delinquency, costInCar) {
  const collected = delinquency?.collected || 0;
  const cost = Number(costInCar) || 0;
  const profit = Math.round((collected - cost) * 100) / 100;
  const gap = Math.max(0, Math.round((cost - collected) * 100) / 100);
  const cashPositive = collected + 0.009 >= cost && cost > 0;
  const payAmt = Number(account.pay_amount) || 0;
  const periodsNeeded = !cashPositive && payAmt > 0 ? Math.ceil(gap / payAmt) : 0;
  const weeksNeeded = periodsNeeded * 2;
  const email = sellerEmail(company);
  const coName = companyDisplayName({
    coLegal: company?.legal_name,
    coDba: company?.dba,
  });
  const vehicle = [account.veh_year, account.veh_make, account.veh_model]
    .filter(Boolean)
    .join(' ');
  const payments = (account.payments || []).filter((p) => !p.voided_at);
  const paymentRows = payments.length
    ? payments
        .slice()
        .sort((a, b) => String(a.paid_on || '').localeCompare(String(b.paid_on || '')))
        .map(
          (p) =>
            `<tr><td>${String(p.paid_on || '').slice(0, 10)}</td><td>${paymentKindLabel(p.kind)}</td><td class="num">${money(p.amount)}</td></tr>`
        )
        .join('')
    : '<tr><td colspan="3">No payments posted.</td></tr>';

  return scrubDashes(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Account P&L</title>
<style>
body{font-family:"Times New Roman",Times,serif;margin:0.65in;font-size:11pt;color:#111;line-height:1.35}
h1{font-size:16pt;margin:0 0 0.2rem;text-align:center}
.sub{text-align:center;font-size:9.5pt;margin-bottom:0.75rem;color:#333}
table{width:100%;border-collapse:collapse;margin-top:0.4rem}
td,th{border:1px solid #444;padding:0.32rem 0.4rem;text-align:left}
th{background:#f0f0f0}
td.num{text-align:right}
.ok{color:#0a5c2b;font-weight:700}
.bad{color:#8a1f1f;font-weight:700}
</style></head><body>
<h1>Account Profit &amp; Loss</h1>
<div class="sub">${coName} · Account #${account.id} · ${email}</div>
<p><strong>${account.buyer_name || '-'}</strong> · ${vehicle || '-'} · VIN ${account.veh_vin || '-'} · Status: ${account.status || 'active'}</p>
<table>
<tr><th>Cost in car</th><td class="num">${money(cost)}</td></tr>
<tr><th>Cash collected (BHPH payments)</th><td class="num">${money(collected)}</td></tr>
<tr><th>Realized profit / (loss)</th><td class="num ${profit >= 0 ? 'ok' : 'bad'}">${money(profit)}</td></tr>
<tr><th>Cash-positive?</th><td>${cashPositive ? 'Yes' : `Not yet - ${money(gap)} remaining to recover cost`}</td></tr>
<tr><th>Est. biweeklies to cash-positive</th><td>${cashPositive ? '0 (already cash-positive)' : periodsNeeded > 0 ? `${periodsNeeded} payments (~${weeksNeeded} weeks) at ${money(payAmt)}` : 'Set biweekly amount to estimate'}</td></tr>
<tr><th>Remaining principal</th><td class="num">${money(delinquency?.remainingPrincipal)}</td></tr>
<tr><th>Original time balance</th><td class="num">${money(account.time_balance)}</td></tr>
<tr><th>Contract cash price</th><td class="num">${money(account.cash_price)}</td></tr>
<tr><th>Down payment</th><td class="num">${money(account.down_payment)}</td></tr>
<tr><th>Finance charge</th><td class="num">${money(account.finance_charge)}</td></tr>
<tr><th>APR</th><td>${account.apr != null ? account.apr : '-'}</td></tr>
<tr><th>Days past due</th><td>${delinquency?.daysPastDue ?? 0}</td></tr>
</table>
<h2 style="font-size:12pt;margin:0.85rem 0 0.25rem">Posted payments</h2>
<table>
<thead><tr><th>Date</th><th>Type</th><th class="num">Amount</th></tr></thead>
<tbody>${paymentRows}</tbody>
</table>
</body></html>`);
}
